import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, DollarSign, Star, Users, Check, X, MessageSquare, MessageCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { formatCurrency } from '../utils/currency';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const TIME_SLOTS = ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

const LawyerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availabilityModal, setAvailabilityModal] = useState(false);
  const [availability, setAvailability] = useState({});

  useEffect(() => {
    fetchDashboardData();
    fetchBookings();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await api.get('/lawyers/dashboard/stats');
      setStats(res.data);
      setAvailability(res.data.profile?.availability || {});
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await api.get('/bookings');
      setBookings(res.data || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await api.put(`/bookings/${bookingId}`, { status });
      toast.success(`Booking ${status}`);
      fetchBookings();
      fetchDashboardData();
    } catch (err) {
      toast.error('Failed to update booking');
    }
  };

  const saveAvailability = async () => {
    try {
      await api.put('/lawyers/availability', { slots: availability });
      toast.success('Availability updated');
      setAvailabilityModal(false);
    } catch (err) {
      toast.error('Failed to update availability');
    }
  };

  const toggleSlot = (day, slot) => {
    setAvailability(prev => {
      const daySlots = prev[day] || [];
      if (daySlots.includes(slot)) {
        return { ...prev, [day]: daySlots.filter(s => s !== slot) };
      }
      return { ...prev, [day]: [...daySlots, slot] };
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      accepted: 'badge-approved',
      declined: 'badge-rejected',
      completed: 'badge-completed',
      cancelled: 'badge-rejected'
    };
    return badges[status] || 'badge-pending';
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const upcomingBookings = bookings.filter(b => b.status === 'accepted');
  const pastBookings = bookings.filter(b => ['completed', 'declined', 'cancelled'].includes(b.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-slate-200 rounded-2xl" />
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-xl" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isApproved = stats?.profile?.is_approved;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Welcome, {user?.name}</h1>
            <p className="text-slate-500 mt-1">Manage your practice</p>
          </div>
          <div className="flex gap-3">
            <Link to="/lawyer/profile/settings">
              <Button 
                variant="outline"
                className="rounded-full"
                data-testid="edit-profile-btn"
              >
                <Settings className="w-5 h-5 mr-2" />
                Edit Profile
              </Button>
            </Link>
            {isApproved && (
              <Button 
                onClick={() => setAvailabilityModal(true)}
                className="bg-amber-600 hover:bg-amber-700 rounded-full"
                data-testid="set-availability-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Set Availability
              </Button>
            )}
          </div>
        </div>

        {/* Approval Status Banner */}
        {!isApproved && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8"
          >
            <h3 className="font-semibold text-amber-800">Profile Pending Approval</h3>
            <p className="text-amber-600 mt-1">
              Your profile is under review by our admin team. You will be notified once approved.
            </p>
          </motion.div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.stats?.total_bookings || 0}</p>
                <p className="text-slate-500 text-sm">Total Clients</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.stats?.pending_bookings || 0}</p>
                <p className="text-slate-500 text-sm">Pending</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.stats?.total_earnings || 0)}</p>
                <p className="text-slate-500 text-sm">Earnings</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.stats?.rating || 0}</p>
                <p className="text-slate-500 text-sm">Rating ({stats?.stats?.review_count || 0})</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bookings */}
        {isApproved && (
          <div className="bg-white rounded-2xl border border-slate-200">
            <Tabs defaultValue="pending">
              <TabsList className="w-full border-b border-slate-200 rounded-t-2xl">
                <TabsTrigger value="pending" className="flex-1" data-testid="tab-pending">
                  Pending ({pendingBookings.length})
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="flex-1" data-testid="tab-upcoming">
                  Upcoming ({upcomingBookings.length})
                </TabsTrigger>
                <TabsTrigger value="past" className="flex-1" data-testid="tab-past">
                  Past ({pastBookings.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="p-6">
                {pendingBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No pending requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingBookings.map((booking, idx) => (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                        data-testid={`pending-booking-${idx}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-slate-900">{booking.client?.name}</h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {booking.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {booking.time_slot}
                              </span>
                              <span className="capitalize">{booking.consultation_mode}</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-500 bg-white p-2 rounded-lg">
                              {booking.case_description}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.id, 'accepted')}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`accept-booking-${idx}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBookingStatus(booking.id, 'declined')}
                              data-testid={`decline-booking-${idx}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="upcoming" className="p-6">
                {upcomingBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No upcoming appointments</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingBookings.map((booking, idx) => (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                        data-testid={`upcoming-booking-${idx}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-slate-900">{booking.client?.name}</h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {booking.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {booking.time_slot}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.id, 'completed')}
                              className="bg-slate-900"
                              data-testid={`complete-booking-${idx}`}
                            >
                              Mark Completed
                            </Button>
                            <Link to={`/chat/booking/${booking.id}`}>
                              <Button
                                size="sm"
                                className="bg-[#075e54] hover:bg-[#054c44]"
                                data-testid={`chat-client-${idx}`}
                              >
                                <MessageCircle className="w-4 h-4 mr-1" />
                                Chat
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="past" className="p-6">
                {pastBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No past consultations</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastBookings.map((booking, idx) => (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                        data-testid={`past-booking-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">{booking.client?.name}</h3>
                            <p className="text-sm text-slate-500">{booking.date} at {booking.time_slot}</p>
                          </div>
                          <span className={getStatusBadge(booking.status)}>{booking.status}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* Availability Modal */}
      <Dialog open={availabilityModal} onOpenChange={setAvailabilityModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Set Your Availability</DialogTitle>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            <div className="space-y-6">
              {DAYS.map(day => (
                <div key={day} className="border-b border-slate-100 pb-4">
                  <h4 className="font-medium text-slate-900 capitalize mb-3">{day}</h4>
                  <div className="flex flex-wrap gap-2">
                    {TIME_SLOTS.map(slot => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => toggleSlot(day, slot)}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          (availability[day] || []).includes(slot)
                            ? 'bg-amber-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`slot-${day}-${slot}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityModal(false)}>
              Cancel
            </Button>
            <Button onClick={saveAvailability} data-testid="save-availability-btn">
              Save Availability
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LawyerDashboard;
